import { Models } from '@rematch/core';

// ENTITIES
import sceneAdminEntity, { storeName as sceneAdminEntityStore } from './entities/admin/sceneAdmin.entity';
import tourAdminEntity, { storeName as tourAdminEntityStore } from './entities/admin/tourAdmin.entity';
import assignedUserContactEntity, {
  storeName as assignedUserContactEntityStore,
} from './entities/assignedUserContact.entity';
import cmsUserContactEntity, { storeName as cmsUserContactEntityStore } from './entities/cmsUserContact.entity';
import webRotateEntity, { storeName as webRotateEntityStore } from './entities/cmsWebRotate.entity';
import draftTourEntity, { storeName as draftTourEntityStoreName } from './entities/draftTour.entity';
import iconSetEntity, { storeName as iconSetEntityStoreName } from './entities/iconSet.entity';
import itemInfoEntity, { storeName as itemInfoEntityStoreName } from './entities/itemInfo.entity';
import mediaEntity, { storeName as mediaEntityStoreName } from './entities/media.entity';
import promotionEntity, { storeName as promotionEntityStoreName } from './entities/promotion.entity';
import sceneEntity, { storeName as sceneEntityStoreName } from './entities/scene.entity';
import themeEntity, { storeName as themeEntityStoreName } from './entities/theme.entity';
import tourEntity, { storeName as tourEntityStoreName } from './entities/tour.entity';
import poiTemplateEntity, {
  storeName as poiTemplateEntityStoreName,
} from './entities/poiTemplate.entity';
import tourGuideEntity, { storeName as tourGuideEntityStoreName } from './entities/tourGuide.entity';
import chatbotEntities, { storeName as chatbotEntityStoreName } from './entities/chatbot.entity';

// MODELS
import sceneAdmin, { storeName as sceneAdminStore } from './models/admin/sceneAdmin.model';
import tourAdmin, { storeName as tourAdminStore } from './models/admin/tourAdmin.model';
import app, { storeName as appStoreName } from './models/app.model';
import assignedUserContacts, { storeName as assignedUserContactStore } from './models/assignedUserContact.model';
import categories, { storeName as categoryStoreName } from './models/category.model';
import sceneGroup, { storeName as sceneGroupStoreName } from './models/sceneGroup.model';
import cmsUserContacts, { storeName as cmsUseContactStore } from './models/cmsUserContact.model';
import webRotate, { storeName as webRotateStore } from './models/cmsWebRotate.model';
import configTourModal, { storeName as configModalStoreName } from './models/configTour/configModal.model';
import tourDetail, { storeName as tourDetailStoreName } from './models/configTour/tourDetail.model';
import draftFloorplans, { storeName as draftFloorplanStoreName } from './models/draftFloorplan.model';
import draftTour, { storeName as draftTourStore } from './models/draftTour.model';
import floorplans, { storeName as floorplanStoreName } from './models/floorplan.model';
import iconSets, { storeName as iconSetStoreName } from './models/iconSet.model';
import itemInfos, { storeName as itemInfoStoreName } from './models/itemInfo.model';
import mapTourGuide, { storeName as mapTourGuideStoreName } from './models/mapTourGuide.model';
import media, { storeName as mediaStoreName } from './models/media/media.model';
import mediaModal, { storeName as mediaModalStoreName } from './models/media/mediaModal.model';
import mergeTours, { storeName as mergeToursStoreName } from './models/mergeTours.model';
import promotions, { storeName as promotionStoreName } from './models/promotion.model';
import scenes, { storeName as sceneStoreName } from './models/scene.model';
import theme, { storeName as themeStoreName } from './models/theme/theme.model';
import themeModal, { storeName as themeModalStoreName } from './models/theme/themeModal.model';
import tours, { storeName as tourStoreName } from './models/tour.model';
import tourBgSounds, { storeName as tourBgSoundStoreName } from './models/tourBgSound.model';
import tourGuides, { storeName as tourGuideStoreName } from './models/tourGuide.model';
import tourModify, { storeName as tourModifyStoreName } from './models/tourModify.model';
import user, { storeName as userStoreName } from './models/user.model';
import userContacts, { storeName as userContactStoreName } from './models/userContacts.model';
import functionButtons, {
  storeName as functionButtonStoreName,
} from './models/functionButton.model';
import poiTemplates, { storeName as poiTemplateStoreName } from './models/poiTemplate.model';
import chatbots, { storeName as chatbotStoreName } from './models/chatbot.model';

export interface RootModel extends Models<RootModel> {
  // MODELS
  app: typeof app;
  configTourModal: typeof configTourModal;
  tourDetail: typeof tourDetail;
  tours: typeof tours;
  scenes: typeof scenes;
  tourGuides: typeof tourGuides;
  tourBgSounds: typeof tourBgSounds;
  tourModify: typeof tourModify;
  mapTourGuide: typeof mapTourGuide;
  userContacts: typeof userContacts;
  promotions: typeof promotions;
  iconSets: typeof iconSets;
  media: typeof media;
  theme: typeof theme;
  cmsUserContacts: typeof cmsUserContacts;
  assignedUserContacts: typeof assignedUserContacts;
  sceneAdmin: typeof sceneAdmin;
  tourAdmin: typeof tourAdmin;
  webRotate: typeof webRotate;
  draftTour: typeof draftTour;
  draftFloorplans: typeof draftFloorplans;
  itemInfos: typeof itemInfos;
  poiTemplates: typeof poiTemplates;
  functionButtons: typeof functionButtons;
  mergeTours: typeof mergeTours;
  chatbots: typeof chatbots;
  // ENTITIES
  sceneEntity: typeof sceneEntity;
  tourEntity: typeof tourEntity;
  draftTourEntity: typeof draftTourEntity;
  tourGuideEntity: typeof tourGuideEntity;
  iconSetEntity: typeof iconSetEntity;
  mediaEntity: typeof mediaEntity;
  themeEntity: typeof themeEntity;
  cmsUserContactEntity: typeof cmsUserContactEntity;
  assignedUserContactEntity: typeof assignedUserContactEntity;
  sceneAdminEntity: typeof sceneAdminEntity;
  tourAdminEntity: typeof tourAdminEntity;
  webRotateEntity: typeof webRotateEntity;
  itemInfoEntity: typeof itemInfoEntity;
  promotionEntity: typeof promotionEntity;
  poiTemplateEntity: typeof poiTemplateEntity;
  chatbotEntities: typeof chatbotEntities;
}

export const models: RootModel = {
  // MODELS
  [userStoreName]: user,
  [appStoreName]: app,
  [configModalStoreName]: configTourModal,
  [tourDetailStoreName]: tourDetail,
  [tourStoreName]: tours,
  [tourModifyStoreName]: tourModify,
  [mapTourGuideStoreName]: mapTourGuide,
  [sceneStoreName]: scenes,
  [floorplanStoreName]: floorplans,
  [draftFloorplanStoreName]: draftFloorplans,
  [categoryStoreName]: categories,
  [sceneGroupStoreName]: sceneGroup,
  [mediaStoreName]: media,
  [mediaModalStoreName]: mediaModal,
  [tourGuideStoreName]: tourGuides,
  [tourBgSoundStoreName]: tourBgSounds,
  [itemInfoStoreName]: itemInfos,
  [themeModalStoreName]: themeModal,
  [themeStoreName]: theme,
  [userContactStoreName]: userContacts,
  [promotionStoreName]: promotions,
  [iconSetStoreName]: iconSets,
  [cmsUseContactStore]: cmsUserContacts,
  [assignedUserContactStore]: assignedUserContacts,
  [sceneAdminStore]: sceneAdmin,
  [tourAdminStore]: tourAdmin,
  [webRotateStore]: webRotate,
  [draftTourStore]: draftTour,
  [functionButtonStoreName]: functionButtons,
  [poiTemplateStoreName]: poiTemplates,
  [mergeToursStoreName]: mergeTours,
  [chatbotStoreName]: chatbots,
  // ENTITIES
  [promotionEntityStoreName]: promotionEntity,
  [sceneEntityStoreName]: sceneEntity,
  [tourEntityStoreName]: tourEntity,
  [tourGuideEntityStoreName]: tourGuideEntity,
  [iconSetEntityStoreName]: iconSetEntity,
  [mediaEntityStoreName]: mediaEntity,
  [themeEntityStoreName]: themeEntity,
  [cmsUserContactEntityStore]: cmsUserContactEntity,
  [assignedUserContactEntityStore]: assignedUserContactEntity,
  [sceneAdminEntityStore]: sceneAdminEntity,
  [tourAdminEntityStore]: tourAdminEntity,
  [webRotateEntityStore]: webRotateEntity,
  [draftTourEntityStoreName]: draftTourEntity,
  [itemInfoEntityStoreName]: itemInfoEntity,
  [poiTemplateEntityStoreName]: poiTemplateEntity,
  [chatbotEntityStoreName]: chatbotEntities,
};
